import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Button, makeStyles, Portal, tokens } from "@fluentui/react-components";
import { CursorClick20Regular, Dismiss16Regular, Map20Regular } from "@fluentui/react-icons";

import { anchorIndex, HELP_PANEL_ATTR, type AnchoredStep } from "./dom";
import { attachInspectListeners } from "./inspect";
import { cardPosition, rectOf, type Rect } from "./position";
import type { HelpLanguage, HelpStep } from "./types";
import { localizedStep } from "./types";
import { ACCENT, INK, SHADOW_CARD, SHADOW_SOFT, uiText, Z } from "./ui";

/**
 * Click-to-explain — "what is THIS field?", answered by pointing at it. The screen itself is
 * the index: a user looking at a field already knows where it is.
 *
 * Three things make it safe to run over a half-filled form:
 *  - **Every page interaction is swallowed** (see `inspect.ts`), so asking "what is this?"
 *    never submits the form, opens a dropdown or moves focus mid-edit.
 *  - **Nothing is dimmed.** A scrim would hide the form being read. The mode announces itself
 *    with a banner, the `help` cursor and an outline.
 *  - **The guide's own surfaces are exempt** (`data-help-panel`), so its buttons keep working.
 */

const CARD_WIDTH = 330;
const CARD_HEIGHT = 190;
/** How long "nothing documented here" stays up before it stops being an answer and starts nagging. */
const MISS_MS = 2200;
/** The inspector's cursor sweep — a field's own `text` cursor would otherwise win. */
const CURSOR_STYLE_ID = "help-inspect-cursor";

const useStyles = makeStyles({
  highlight: {
    position: "fixed",
    borderRadius: "6px",
    pointerEvents: "none",
    transitionProperty: "top, left, width, height",
    transitionDuration: "80ms",
    transitionTimingFunction: "ease",
  },
  banner: {
    position: "fixed",
    top: "14px",
    left: "50%",
    transform: "translateX(-50%)",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    backgroundColor: INK,
    color: "#ffffff",
    borderRadius: "999px",
    padding: "4px 6px 4px 16px",
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.35)",
    maxWidth: "calc(100vw - 32px)",
    boxSizing: "border-box",
  },
  bannerLabel: {
    fontSize: "12.5px",
    fontWeight: tokens.fontWeightSemibold,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  bannerEsc: {
    fontSize: "11px",
    opacity: 0.65,
    flexShrink: 0,
    "@media (max-width: 600px)": { display: "none" },
  },
  bannerClose: { color: "#ffffff", flexShrink: 0, minWidth: "auto" },
  miss: {
    position: "fixed",
    width: "240px",
    boxSizing: "border-box",
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: "12px",
    padding: "8px 12px",
    boxShadow: SHADOW_SOFT,
    pointerEvents: "none",
    fontSize: "12px",
    color: tokens.colorNeutralForeground2,
    lineHeight: 1.5,
  },
  card: {
    position: "fixed",
    width: `${CARD_WIDTH}px`,
    boxSizing: "border-box",
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    borderRadius: "16px",
    boxShadow: SHADOW_CARD,
    padding: "16px",
  },
  section: {
    fontSize: "10px",
    fontWeight: tokens.fontWeightBold,
    letterSpacing: "0.8px",
    textTransform: "uppercase",
    color: tokens.colorNeutralForegroundDisabled,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  title: { fontSize: "14.5px", fontWeight: tokens.fontWeightSemibold, marginTop: "2px" },
  body: {
    fontSize: "13px",
    color: tokens.colorNeutralForeground2,
    marginTop: "6px",
    lineHeight: 1.55,
  },
  hint: {
    fontSize: "11.5px",
    color: tokens.colorNeutralForegroundDisabled,
    marginTop: "6px",
    fontStyle: "italic",
    lineHeight: 1.5,
  },
  actions: { display: "flex", alignItems: "center", gap: "8px", marginTop: "14px" },
  continue: { fontSize: "11.5px", marginLeft: "-4px" },
  spacer: { flex: 1 },
});

/** Outline drawn over a control. Never interactive — the real element has to stay hit-testable. */
const Highlight = ({ rect, pinned }: { rect: Rect; pinned?: boolean }) => {
  const styles = useStyles();
  return (
    <div
      aria-hidden
      className={styles.highlight}
      style={{
        top: rect.top - 3,
        left: rect.left - 3,
        width: rect.width + 6,
        height: rect.height + 6,
        zIndex: Z.spotlight,
        border: pinned ? `2px solid ${tokens.colorBrandStroke1}` : `2px dashed ${ACCENT}`,
        backgroundColor: pinned ? "rgba(35, 85, 146, 0.06)" : "transparent",
      }}
    />
  );
};

/**
 * The `help` cursor everywhere except the guide's own surfaces. A raw <style> element rather than
 * Griffel: the rule targets `body *`, which a class-scoped makeStyles cannot express.
 */
const useInspectCursor = (): void => {
  useEffect(() => {
    const style = document.createElement("style");
    style.id = CURSOR_STYLE_ID;
    style.textContent =
      "body * { cursor: help !important; }" +
      `[${HELP_PANEL_ATTR}] * { cursor: auto !important; }` +
      `[${HELP_PANEL_ATTR}] button, [${HELP_PANEL_ATTR}] button * { cursor: pointer !important; }`;
    document.head.appendChild(style);
    return () => style.remove();
  }, []);
};

interface HelpInspectProps {
  /** Role-filtered steps for the current page — the same list the tour and drawer are given. */
  steps: HelpStep[];
  /** `PageHelp.contexts`, so a pinned step can still say how its control is reached. */
  contexts?: Record<string, string>;
  language: HelpLanguage;
  /** Continue as a normal walk from the field just explained. */
  onTourFrom: (step: HelpStep) => void;
  onExit: () => void;
}

export const HelpInspect = ({
  steps,
  contexts,
  language,
  onTourFrom,
  onExit,
}: HelpInspectProps) => {
  const styles = useStyles();
  const text = uiText(language);
  const index = useMemo(() => anchorIndex(steps), [steps]);
  useInspectCursor();

  const [hover, setHover] = useState<AnchoredStep | null>(null);
  const [hoverRect, setHoverRect] = useState<Rect | null>(null);
  const [pinned, setPinned] = useState<AnchoredStep | null>(null);
  const [pinnedRect, setPinnedRect] = useState<Rect | null>(null);
  const [miss, setMiss] = useState<{ top: number; left: number } | null>(null);

  /** The hovered control, so a scroll can re-measure its outline. */
  const hoveredEl = useRef<HTMLElement | null>(null);
  const missTimer = useRef(0);
  /** Read through a ref so the pointer listeners are not re-attached on every parent render. */
  const exitRef = useRef(onExit);
  exitRef.current = onExit;

  const remeasure = useCallback((): void => {
    if (hoveredEl.current) setHoverRect(rectOf(hoveredEl.current));
    setPinnedRect((current) => (pinned && current ? rectOf(pinned.element) : current));
  }, [pinned]);

  // Split from the interaction listeners: this pair depends on what is pinned, the pointer
  // listeners must not be torn down every time something is.
  useEffect(() => {
    window.addEventListener("resize", remeasure);
    window.addEventListener("scroll", remeasure, true);
    return () => {
      window.removeEventListener("resize", remeasure);
      window.removeEventListener("scroll", remeasure, true);
    };
  }, [remeasure]);

  useEffect(() => {
    const detach = attachInspectListeners(index, {
      onHover: (found) => {
        hoveredEl.current = found?.element ?? null;
        setHover(found);
        setHoverRect(found ? rectOf(found.element) : null);
      },
      onPin: (found) => {
        setMiss(null);
        setPinned(found);
        setPinnedRect(rectOf(found.element));
      },
      onMiss: (at) => {
        window.clearTimeout(missTimer.current);
        setMiss(at);
        missTimer.current = window.setTimeout(() => setMiss(null), MISS_MS);
      },
      onExit: () => exitRef.current(),
    });
    const timer = missTimer;
    return () => {
      window.clearTimeout(timer.current);
      detach();
    };
  }, [index]);

  const card = pinnedRect
    ? cardPosition(pinnedRect, { width: CARD_WIDTH, height: CARD_HEIGHT })
    : null;
  const contextHint = pinned?.step.context ? contexts?.[pinned.step.context] : undefined;
  const showHover = hoverRect !== null && hover?.element !== pinned?.element;
  const pinnedText = pinned ? localizedStep(pinned.step, language) : null;

  return (
    <Portal>
      {showHover && hoverRect && <Highlight rect={hoverRect} />}
      {pinnedRect && <Highlight rect={pinnedRect} pinned />}

      <div
        {...{ [HELP_PANEL_ATTR]: "inspect-banner" }}
        className={styles.banner}
        style={{ zIndex: Z.banner }}
      >
        <CursorClick20Regular style={{ flexShrink: 0 }} />
        <span className={styles.bannerLabel}>
          {hover ? localizedStep(hover.step, language).title : text.inspectBanner}
        </span>
        <span className={styles.bannerEsc}>{text.inspectEsc}</span>
        <Button
          appearance="transparent"
          size="small"
          icon={<Dismiss16Regular />}
          onClick={onExit}
          aria-label={text.inspectLeave}
          className={styles.bannerClose}
        />
      </div>

      {miss && (
        <div
          aria-live="polite"
          {...{ [HELP_PANEL_ATTR]: "inspect-miss" }}
          className={styles.miss}
          style={{
            top: Math.min(miss.top + 14, window.innerHeight - 60),
            left: Math.min(miss.left + 14, window.innerWidth - 260),
            zIndex: Z.banner,
          }}
        >
          {text.inspectMiss}
        </div>
      )}

      {pinned && card && pinnedText && (
        <div
          role="dialog"
          {...{ [HELP_PANEL_ATTR]: "inspect" }}
          aria-label={text.inspectCardAria(pinnedText.title)}
          className={styles.card}
          style={{ top: card.top, left: card.left, zIndex: Z.card }}
        >
          {pinned.step.section && <div className={styles.section}>{pinned.step.section}</div>}
          <div className={styles.title}>{pinnedText.title}</div>
          <div className={styles.body}>{pinnedText.body}</div>
          {contextHint && <div className={styles.hint}>{contextHint}</div>}

          <div className={styles.actions}>
            <Button
              appearance="transparent"
              size="small"
              icon={<Map20Regular />}
              onClick={() => onTourFrom(pinned.step)}
              data-help="help-inspect-tour"
              className={styles.continue}
            >
              {text.continueHere}
            </Button>
            <span className={styles.spacer} />
            <Button appearance="primary" size="small" onClick={onExit}>
              {text.done}
            </Button>
          </div>
        </div>
      )}
    </Portal>
  );
};
